import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollableContent } from 'src/components/ScrollableContent';
import { Typography, Input, Flex, Table } from 'antd';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  assignResourceToPoData,
  fetchAllPos,
  searchAssignedDateRangeByEmail,
  searchPoDetailsByPoNumber,
  updateAndDeleteResourceSplitDate,
} from 'src/redux/features/po/ResourceMapping/Action';
import { getAllPOs } from 'src/redux/features/po/ResourceMapping/Slice';
const { Title } = Typography;

import { IPurchaseOrder } from '../../models/PurchaseOrder';
import {
  formatDate,
  formattedDateUS,
  formatToastMsg,
  parseLocalDate,
  toYYYYMMDD,
} from 'src/utils/helpers';
import { useAuth } from 'react-oidc-context';
import dayjs from 'dayjs';
import {
  UpOutlined,
  DownOutlined,
  EditOutlined,
  MinusCircleFilled,
  SearchOutlined,
  PlusCircleFilled,
} from '@ant-design/icons';
import SharedDatePicker from 'src/components/DatePicker';
import SearchableDropdown from 'src/components/SearchableDropdown';
import ResourceSearchableDropdown from 'src/components/ResourceSearch';
import AntDButton from 'src/components/Button';
import ResourceWorkingHoursModal from 'src/components/ResourceWorkingHourModal';
import CustomSplitter from 'src/components/Splitter';
import PODetailTable from 'src/components/PODetailTable';
import { IAllPurchaseOrderResp } from 'src/models/AllPurchaseOrder';
import en from 'src/messages/en.json';
import { theme } from 'antd';
import { setError } from 'src/redux/features/common/commonSlice';
const ManagePos = () => {
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const auth = useAuth();
  const [data, setData] = useState<IPurchaseOrder[]>([]);
  const [purchaseData, setPurchaseData] = useState<IAllPurchaseOrderResp[]>([]);
  const [filteredData, setFilteredData] = useState<IAllPurchaseOrderResp[]>([]);

  const [vendors, setVendors] = useState<string[]>([]);
  const [poNumbers, setPoNumbers] = useState<number[]>([]);
  const [poDescription, setPoDescription] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedPo, setSelectedPo] = useState('');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [jobResources, setJobResources] = useState([]);
  const [isResourceDataLoading, setIsResourceDataLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPOTableLoading, setIsPOTableLoading] = useState(false);
  const [isSecondPanelVisible, setIsSecondPanelVisible] =
    useState(!!selectedPo);

  const [selectedResource, setSelectedResource] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [totalCount, setTotalCount] = useState(0);

  const [allResourceData, setAllResourceData] = useState<
    { resource: string; data: any }[]
  >([]);
  const [disabledDateRanges, setDisabledDateRanges] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchValueByVendor, setSearchValueByVendor] = useState('');

  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<any[]>([]);
  const [editResourcePoNumber, setEditResourcePoNumber] = useState('');
  const activeRequest = useRef<any>(null);

  const handleOpenModal = (resource: any, poNumber: string) => {
    setSelectedResource(resource?.resource);
    setModalData([resource]);
    setEditResourcePoNumber(poNumber);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditResourcePoNumber('');
    setModalData(null);
  };

  useEffect(() => {
    if (data) {
      setIsResourceDataLoading(true);
      const allPOLines = data
        ?.flatMap((po) =>
          po?.purchaseOrderHeaders?.flatMap(
            (header) => header?.purchaseOrderLines,
          ),
        )
        ?.sort((a, b) => a.lineNum - b.lineNum);

      const updatedPOLines = allPOLines?.map((item) => ({
        ...item,
        resources:
          item?.resources?.length > 0
            ? item.resources.map((res) => ({
                ...res,
                startDate: res.startDate ? toYYYYMMDD(res.startDate) : '',
                endDate: res.endDate ? toYYYYMMDD(res.endDate) : '',
              }))
            : [{ resource: 0, name: '', startDate: '', endDate: '' }],
      }));

      setJobResources(updatedPOLines);
      setIsResourceDataLoading(false);
      setExpandedItems([]);
    }
  }, [data]);

  const { data: projects } = useSelector(getAllPOs);

  // date range picker
  const handleDateChange = (
    itemIndex: number,
    resourceIndex: number,
    field: 'startDate' | 'endDate',
    value: string,
  ) => {
    const isoDate = value ? toYYYYMMDD(value) : null;

    setJobResources((prevResources) =>
      prevResources?.map((item, i) =>
        i === itemIndex
          ? {
              ...item,
              resources: item?.resources?.map((res, rIndex) =>
                rIndex === resourceIndex ? { ...res, [field]: isoDate } : res,
              ),
            }
          : item,
      ),
    );
  };
  // add new resource
  const handleAddResource = (lineItemId: number) => {
    setJobResources((prevData) =>
      prevData?.map((item) =>
        (Array.isArray(item?.poLineId) &&
          item?.poLineId.includes(lineItemId)) ||
        item?.poLineId === lineItemId
          ? {
              ...item,
              resources: [
                ...item?.resources,
                {
                  id: 0,
                  resource: '',
                  name: '',
                  startDate: '',
                  endDate: '',
                },
              ],
            }
          : item,
      ),
    );
    if (!expandedItems.includes(lineItemId)) {
      setExpandedItems((prev) => [...prev, lineItemId]);
    }
  };

  const onSelectedResourceChange = (selected, index, resourceIndex) => {
    if (!selected) {
      setSelectedResource('');
      setJobResources((prevResources) =>
        prevResources.map((item, i) => {
          if (i !== index) return item;

          const updatedResources = item.resources ? [...item.resources] : [];

          updatedResources[resourceIndex] = {
            ...updatedResources[resourceIndex],
            resource: '',
            name: '',
          };

          return { ...item, resources: updatedResources };
        }),
      );

      return;
    }
    setSelectedResource(selected.value);

    setJobResources((prevResources) =>
      prevResources.map((item, i) => {
        if (i !== index) return item;

        const updatedResources = [...(item.resources || [])];

        updatedResources[resourceIndex] = {
          ...updatedResources[resourceIndex],
          resource: selected.value,
          name: selected.label,
          startDate: updatedResources[resourceIndex]?.startDate || '',
          endDate: updatedResources[resourceIndex]?.endDate || '',
        };

        return { ...item, resources: updatedResources };
      }),
    );
  };

  // remove reource
  const handleRemoveResource = (poLineId: number, index: number) => {
    setJobResources((prevData) =>
      prevData?.map((item) => {
        if (item.poLineId !== poLineId) return item;

        return {
          ...item,
          resources:
            item.resources.length > 1
              ? item.resources.filter((_, i) => i !== index)
              : item.resources.map((res, i) =>
                  i === index
                    ? {
                        ...res,
                        resource: null,
                        name: null,
                        startDate: '',
                        endDate: '',
                      }
                    : res,
                ),
        };
      }),
    );

    const deletedResource = jobResources.find(
      (item) => item.poLineId === poLineId,
    )?.resources[index];

    const flatedArray = jobResources?.flatMap((val) => val.resources);
    const foundResource = flatedArray?.find(
      (val) => val.id?.toString() === deletedResource?.id?.toString(),
    );

    const payload = {
      purchaseOrder: selectedPo.toString(),
      itLeaderEmail: auth.user.profile.email,
      payload: {
        poDescription,
        lineItems: [
          {
            poLineId: poLineId,
            resources: [{ id: foundResource?.id }],
          },
        ],
      },
    };

    if (foundResource?.id > 0) {
      onSave(payload, true);

      return toast.success(en.POs.Success.resourceRemoved);
    } else {
      return;
    }
  };

  //expan & collapse
  const toggleItem = (id: number) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };
  const fetchProjects = async () => {
    if (!selectedPo) {
      setData([]);

      return;
    }
    setIsLoading(true);
    try {
      const response = await dispatch(
        searchPoDetailsByPoNumber({ purchaseOrder: selectedPo }),
      );

      setData(response.payload ? [response.payload] : []);
    } catch (error) {
      dispatch(setError(en.Common.Error.fetchData));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDateRange = async () => {
    if (!selectedResource) {
      return;
    }
    try {
      const response = await dispatch(
        searchAssignedDateRangeByEmail({ email: selectedResource }),
      );

      if (
        !response.payload ||
        !Array.isArray(response.payload) ||
        response.payload.length === 0
      ) {
        return;
      }

      setAllResourceData((prevData) => {
        if (prevData.some((item) => item.resource === selectedResource)) {
          return prevData;
        }

        return [
          ...prevData,
          { resource: selectedResource, data: response.payload },
        ];
      });
    } catch (error) {
      dispatch(setError(en.POs.Error.fetchDateRange));
    }
  };
  const onSave = async (Payload?: any, isDelete?: boolean) => {
    setIsSaving(true);

    try {
      const response = await dispatch(assignResourceToPoData(Payload)).unwrap();

      if (response?.message) {
        dispatch(setError(response.message));

        return;
      }

      setAllResourceData([]);
      fetchProjects();
      setData([response?.payload]);
      setSelectedResource('');

      if (isDelete !== true) {
        toast.success(en.POs.Success.resourceMapped);

        await fetchData({
          poNumber: searchValue,
          vendorName: searchValueByVendor,
          startDate: startDate,
          endDate: endDate,
          pageNumber: pageNumber,
          pageSize: pageSize,
          sortField: sortField,
          sortDirection: sortDirection,
        });
      }
    } catch (error: any) {
      const errorMessage =
        typeof error === 'string' ? error : en.Common.Error.refresh;

      dispatch(setError(errorMessage));
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmit = () => {
    const assignedDates: Record<
      string,
      { startDate: string; endDate: string }[]
    > = {};

    for (const item of jobResources) {
      for (const res of item.resources) {
        if (res.resource) {
          if (!res.startDate) {
            return dispatch(
              setError(
                formatToastMsg(en.POs.Error.selectStartDate, {
                  resource: res.resource,
                }),
              ),
            );
          }
          if (!res.endDate) {
            return dispatch(
              setError(
                formatToastMsg(en.POs.Error.selectEndDate, {
                  resource: res.resource,
                }),
              ),
            );
          }
          if (res.startDate > res.endDate) {
            return dispatch(setError(en.Common.Error.startDateGreater));
          }
        }

        // Convert start and end dates to timestamps for comparison
        const startTimestamp = new Date(res.startDate).getTime();
        const endTimestamp = new Date(res.endDate).getTime();

        // **Check for overlapping dates**
        if (assignedDates[res.resource]) {
          for (const {
            startDate: prevStart,
            endDate: prevEnd,
          } of assignedDates[res.resource]) {
            const prevStartTimestamp = new Date(prevStart).getTime();
            const prevEndTimestamp = new Date(prevEnd).getTime();

            if (
              (startTimestamp >= prevStartTimestamp &&
                startTimestamp <= prevEndTimestamp) ||
              (endTimestamp >= prevStartTimestamp &&
                endTimestamp <= prevEndTimestamp) ||
              (startTimestamp <= prevStartTimestamp &&
                endTimestamp >= prevEndTimestamp)
            ) {
              return dispatch(
                setError(
                  formatToastMsg(en.POs.Error.resourceAlreadyAssigned, {
                    resource: res.resource,
                    startDate: formattedDateUS(prevStartTimestamp),
                    endDate: formattedDateUS(prevEndTimestamp),
                  }),
                ),
              );
            }
          }
        }

        // Store the assigned date range
        if (!assignedDates[res.resource]) {
          assignedDates[res.resource] = [];
        }
        assignedDates[res.resource].push({
          startDate: res.startDate,
          endDate: res.endDate,
        });
      }
    }

    const payload = {
      purchaseOrder: selectedPo.toString(),
      itLeaderEmail: auth.user.profile.email,
      payload: {
        poDescription,
        lineItems: jobResources?.map(({ poLineId, resources }) => ({
          poLineId,
          resources: [
            ...resources?.filter(
              (r) => r?.resource && String(r?.resource)?.trim() !== '',
            ),
          ],
        })),
      },
    };

    onSave(payload);
  };
  const handleSaveSplitDates = async (updatedData: any[]) => {
    const splitDates = updatedData[0]?.splitDates || [];

    const hasMissingStartDate = splitDates.some((item: any) => !item.startDate);
    const hasMissingEndDate = splitDates.some((item: any) => !item.endDate);

    if (hasMissingStartDate) {
      dispatch(setError(en.Common.Error.enterStartDate));

      return;
    }

    if (hasMissingEndDate) {
      dispatch(setError(en.Common.Error.enterEndDate));

      return;
    }
    try {
      setIsSaving(true);
      const response = await dispatch(
        updateAndDeleteResourceSplitDate({
          purchaseOrder: selectedPo,
          id: updatedData[0]?.id,
          payload: updatedData[0]?.splitDates,
        }),
      ).unwrap();

      if (response?.message) {
        dispatch(setError(response.message));
      } else {
        toast.success(en.POs.Success.resourceDetailsUpdated);
        setAllResourceData([]);
        setSelectedResource('');
        fetchProjects();
        setIsModalVisible(false);
      }
    } catch (error) {
      dispatch(setError(en.Common.Error.saveData));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRangeDateChange = (
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleSelectionVendorChange = (selected: string | null) => {
    setSelectedVendor(selected ?? null);
  };

  const handleSelectionPoNumberChange = (selected: number | null) => {
    setSelectedPo(selected?.toString() ?? undefined);
  };
  const getAdjustedDisabledRanges = (excludedRanges, currentDate) => {
    if (!currentDate) return excludedRanges;

    const normalize = (date) => {
      const d = new Date(date);

      d.setHours(0, 0, 0, 0);

      return d;
    };

    const current = normalize(currentDate).getTime();

    return excludedRanges.filter((range) => {
      const start = normalize(range.start).getTime();
      const end = normalize(range.end).getTime();

      return !(current >= start && current <= end);
    });
  };

  const mainColumns = [
    {
      title: en.POs.Title.line,
      dataIndex: 'lineNum',
      key: 'lineNum',
      width: '6%',
      className: 'align-top-cell',
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: '2.5rem',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: en.POs.Title.itemDescription,
      dataIndex: 'itemDescription',
      key: 'itemDescription',
      width: '18%',
      className: 'align-top-cell',
      ellipsis: true,
      render: (text) => (
        <div
          style={{ display: 'flex', alignItems: 'center', minHeight: '2.5rem' }}
        >
          <Typography.Text ellipsis style={{ width: '100%' }} title={text}>
            {text}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: en.POs.Title.quantity,
      dataIndex: 'quantity',
      key: 'quantity',
      width: '7%',
      className: 'align-top-cell',
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            minHeight: '2.5rem',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: '',
      key: 'toggleIcon',
      width: '4%',
      className: 'align-top-cell',
      render: (_, record) =>
        (record?.resources?.length ?? 0) > 1 && (
          <span
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '2.5rem',
            }}
            onClick={() => toggleItem(record.poLineId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleItem(record.poLineId);
              }
            }}
            aria-label="Toggle resource visibility"
          >
            {expandedItems.includes(record.poLineId) ? (
              <UpOutlined style={{ marginRight: 4 }} />
            ) : (
              <DownOutlined style={{ marginRight: 4 }} />
            )}
          </span>
        ),
    },
    {
      title: '',
      key: 'icon',
      width: '4%',
      className: 'align-top-cell',
      render: (_, record) => {
        const isExpanded =
          expandedItems.includes(record.poLineId) ||
          record.resources.length <= 1;

        return (
          isExpanded && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '2.5rem',
              }}
            >
              <PlusCircleFilled
                onClick={() => handleAddResource(record?.poLineId)}
                title="Add Resource"
                style={{
                  color: token.colorPrimary,
                }}
              />
            </div>
          )
        );
      },
    },
    {
      title: en.POs.Title.resource,
      dataIndex: 'resources',
      key: 'resource',
      width: '15%',
      ellipsis: true,
      render: (resources: any[], record: any, index: number) => {
        const isExpanded = expandedItems.includes(record.poLineId);
        const resourcesToRender = isExpanded
          ? resources
          : resources.slice(0, 1);

        return (
          <div>
            {resourcesToRender.map((res, resourceIndex) => (
              <div
                key={`${record.poLineId}-${resourceIndex}`}
                role="button"
                tabIndex={0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '2.5rem',
                }}
              >
                <ResourceSearchableDropdown
                  placeholder={en.Common.Placeholder.selectResource}
                  selected={
                    res?.name && res?.resource
                      ? { label: res.name, value: res.resource }
                      : null
                  }
                  onChange={(selected: any) =>
                    onSelectedResourceChange(selected, index, resourceIndex)
                  }
                  internalResourceFlag={false}
                  disabled={
                    // !res.name ||
                    res?.splitDates?.length > 1 ||
                    (res?.splitDates?.length === 1 &&
                      res?.splitDates[0].status === true)
                  }
                />
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: en.POs.Title.fromDate,
      dataIndex: 'resources',
      key: 'startDate',
      width: '12%',
      ellipsis: true,
      render: (resources: any[], record: any, itemIndex: number) => {
        const isExpanded = expandedItems.includes(record.poLineId);
        const resourcesToRender = isExpanded
          ? resources
          : resources.slice(0, 1);

        return (
          <div>
            {resourcesToRender.map((res, resourceIndex) => {
              return (
                <div
                  key={`${record.poLineId}-${resourceIndex}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '2.5rem',
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedResource(res?.resource)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedResource(res?.resource);
                    }
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <SharedDatePicker
                      date={
                        res?.startDate &&
                        parseLocalDate(res.startDate) instanceof Date &&
                        !isNaN(parseLocalDate(res.startDate).getTime())
                          ? parseLocalDate(res.startDate)
                          : null
                      }
                      onChange={(date) => {
                        const newStartDate = date
                          ? dayjs(date).format('YYYY-MM-DD')
                          : '';

                        handleDateChange(
                          itemIndex,
                          resourceIndex,
                          'startDate',
                          newStartDate,
                        );
                        if (res?.endDate && newStartDate > res.endDate) {
                          handleDateChange(
                            itemIndex,
                            resourceIndex,
                            'endDate',
                            '',
                          );
                        }
                      }}
                      mode="date"
                      placeholder={en.Timesheet.Placeholder.selectStartDate}
                      dateFormat="MM/DD/YYYY"
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' || e.key === 'Delete') {
                          handleDateChange(
                            itemIndex,
                            resourceIndex,
                            'startDate',
                            '',
                          );
                        }
                      }}
                      disabled={
                        !res.name ||
                        res?.splitDates?.length > 1 ||
                        (res?.splitDates?.length === 1 &&
                          res?.splitDates[0].status === true)
                      }
                      configDate={new Date(data[0]?.configDate)}
                      allowClear={res?.startDate ? null : undefined}
                      // excludeDateIntervals={disabledDateRanges}
                      excludeDateIntervals={getAdjustedDisabledRanges(
                        disabledDateRanges,
                        res?.initialStartDate ? res.initialStartDate : null,
                      )}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: en.POs.Title.toDate,
      dataIndex: 'resources',
      key: 'endDate',
      width: '12%',
      ellipsis: true,
      render: (resources: any[], record: any, itemIndex: number) => {
        const isExpanded = expandedItems.includes(record.poLineId);
        const resourcesToRender = isExpanded
          ? resources
          : resources.slice(0, 1);

        return (
          <div>
            {resourcesToRender.map((res, resourceIndex) => (
              <div
                key={`${record.poLineId}-${resourceIndex}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '2.5rem',
                }}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedResource(res?.resource)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedResource(res?.name);
                  }
                }}
              >
                <div style={{ flex: 1 }}>
                  <SharedDatePicker
                    date={
                      res?.endDate &&
                      parseLocalDate(res.endDate) instanceof Date &&
                      !isNaN(parseLocalDate(res.endDate).getTime())
                        ? parseLocalDate(res.endDate)
                        : null
                    }
                    onChange={(date) => {
                      const newEndDate = date
                        ? dayjs(date).format('YYYY-MM-DD')
                        : '';

                      handleDateChange(
                        itemIndex,
                        resourceIndex,
                        'endDate',
                        newEndDate,
                      );
                    }}
                    placeholder={en.Timesheet.Placeholder.selectEndDate}
                    dateFormat="MM/DD/YYYY"
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        handleDateChange(
                          itemIndex,
                          resourceIndex,
                          'endDate',
                          '',
                        );
                      }
                    }}
                    // excludeDateIntervals={disabledDateRanges}
                    excludeDateIntervals={getAdjustedDisabledRanges(
                      disabledDateRanges,
                      res?.initialEndDate ? res.initialEndDate : null,
                    )}
                    disabled={
                      !res.name ||
                      res?.splitDates?.length > 1 ||
                      (res?.splitDates?.length === 1 &&
                        res?.splitDates[0].status === true)
                    }
                    allowClear={res?.endDate ? null : undefined}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'resources',
      key: 'removeAction',
      width: '4%',
      render: (resources: any[], record: any) => {
        const isExpanded = expandedItems.includes(record.poLineId);
        const resourcesToRender = isExpanded
          ? resources
          : resources.slice(0, 1);

        return (
          <div>
            {resourcesToRender.map((resource, resourceIndex) => {
              const hasDates =
                resource?.name ||
                resource?.startDate ||
                resource?.endDate ||
                resourcesToRender.length > 1;
              const isDisabled =
                resource?.splitDates?.length === 1 &&
                resource?.splitDates[0].status === true;

              return (
                <div
                  key={`${record.poLineId}-${resourceIndex}-remove`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '2.5rem',
                    justifyContent: 'center',
                  }}
                >
                  {resource.splitDates && resource.splitDates.length > 1 ? (
                    <EditOutlined
                      style={{ fontSize: '20px', cursor: 'pointer' }}
                      onClick={() => handleOpenModal(resource, selectedPo)}
                      title="Edit Split Dates"
                    />
                  ) : (
                    <MinusCircleFilled
                      style={{
                        color: !isDisabled && hasDates ? '#ff4d4f' : '#d9d9d9',
                        cursor:
                          !isDisabled && hasDates ? 'pointer' : 'not-allowed',
                      }}
                      title={!isDisabled && hasDates ? 'Remove Resource' : ''}
                      onClick={() => {
                        if (!isDisabled && hasDates) {
                          handleRemoveResource(record.poLineId, resourceIndex);
                        }
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];
  const fetchData = async ({
    poNumber = '',
    vendorName = '',
    startDate = null,
    endDate = null,
    pageNumber = 1,
    pageSize = 20,
    sortField = '',
    sortDirection = 'asc',
  }) => {
    setIsPOTableLoading(true);
    if (activeRequest.current) {
      activeRequest.current.abort();
    }
    let promise: any;

    try {
      const rawPayload = {
        PoNumber: poNumber || undefined,
        VendorName: vendorName || undefined,
        StartDate: startDate
          ? dayjs(startDate).format('YYYY-MM-DD')
          : undefined,
        EndDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        PageNumber: pageNumber,
        PageSize: pageSize,
        SortField: sortField || undefined,
        SortDirection: sortDirection,
      };

      const payload: Record<string, any> = {};

      Object.entries(rawPayload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          payload[key] = value;
        }
      });
      promise = dispatch(fetchAllPos(payload));

      activeRequest.current = promise; // store latest promise

      const data = await promise.unwrap();

      if (
        data.totalCount > 0 &&
        payload.pageNumber > Math.ceil(data.totalCount / payload.pageSize)
      ) {
        // If user is on invalid page, go back to last valid page
        setPageNumber(1);
      } else {
        setPurchaseData(data.data);
        setTotalCount(data.totalCount);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Previous request canceled');
      } else {
        console.error('Fetch failed:', err);
      }
    } finally {
      if (activeRequest.current === promise) {
        setIsPOTableLoading(false);
      }
    }
  };

  const updateDisabledDateRanges = () => {
    if (!jobResources?.length || !allResourceData?.length) return;
    const allResources = jobResources?.flatMap((item) =>
      item?.resources?.map((resource) => resource?.resource),
    );

    if (selectedResource || allResources) {
      const assignedResource = allResourceData.find((d) =>
        Array.isArray(d.resource)
          ? d?.resource?.some((res) => allResources?.includes(res))
          : d?.resource === selectedResource,
      );

      if (assignedResource) {
        const formattedRanges = assignedResource?.data
          ?.filter((d) => d?.startDate && d?.endDate)
          ?.map((d) => ({
            start: dayjs(d.startDate).format(),
            end: dayjs(d.endDate).format(),
          }));

        setDisabledDateRanges(formattedRanges);
      } else {
        setDisabledDateRanges([]);
      }
    }
  };

  const LeftPanelContent = () => (
    <PODetailTable
      data={purchaseData}
      setSelectedPO={setSelectedPo}
      searchValue={searchValue}
      searchValueByVendor={searchValueByVendor}
      startDate={startDate}
      endDate={endDate}
      setSearchValue={setSearchValue}
      setSearchValueByVendor={setSearchValueByVendor}
      handleRangeDateChange={handleRangeDateChange}
      isPOTableLoading={isPOTableLoading}
      setIsSecondPanelVisible={setIsSecondPanelVisible}
      selectedPO={selectedPo}
      pageNumber={pageNumber}
      setPageNumber={setPageNumber}
      pageSize={pageSize}
      setPageSize={setPageSize}
      totalCount={totalCount}
      sortField={sortField}
      setSortField={setSortField}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      setTotalCount={setTotalCount}
    />
  );

  const RightPanelContent = () => (
    <div style={{ padding: '0.25rem 0 0 1rem' }}>
      {/* PO Details Heading */}
      <Title level={5} style={{ textAlign: 'left' }}>
        {en.POs.Title.poDetails}
      </Title>

      {/* PO Number & Description */}
      <Flex
        align="center"
        gap={'middle'}
        wrap="nowrap"
        style={{ padding: '0 0 0.75rem' }}
      >
        <Typography.Text strong>
          {en.POs.Label.poNumber}{' '}
          <span style={{ fontWeight: 'normal' }}> {selectedPo} </span>
        </Typography.Text>
        <Typography.Text strong>{en.POs.Label.poDescription}</Typography.Text>
        <Input
          placeholder={en.POs.Placeholder.enterPoDescription}
          value={selectedPo ? poDescription : ''}
          onChange={(e) => setPoDescription(e.target.value)}
          disabled={!selectedPo}
          allowClear
          style={{
            flex: 1,
            maxWidth: 300,
            minWidth: 10,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        />
      </Flex>
      <ScrollableContent
        minHeight={'calc(100vh - 16.25rem)'}
        maxHeight={'calc(100vh - 16.25rem)'}
      >
        <Table
          rowKey="id"
          dataSource={selectedPo && jobResources}
          columns={mainColumns}
          pagination={false}
          sticky
          loading={isLoading}
          bordered={false}
          size="small"
        />
      </ScrollableContent>

      <ResourceWorkingHoursModal
        open={isModalVisible}
        onClose={handleCloseModal}
        data={modalData}
        isManagePO={true}
        poNumber={editResourcePoNumber}
        dateRanges={disabledDateRanges}
        onSave={handleSaveSplitDates}
        configDate={new Date(data[0]?.configDate)}
        isSaving={isSaving}
      />
      <div
        style={{
          marginTop: '0.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <AntDButton
          type="primary"
          onClick={onSubmit}
          disabled={!selectedPo}
          loading={isSaving}
          status="Saving..."
          statusOnComplete="Save"
          htmlType="button"
        ></AntDButton>
      </div>
    </div>
  );

  useEffect(() => {
    fetchProjects();
  }, [dispatch, selectedPo]);
  useEffect(() => {
    fetchDateRange();
  }, [dispatch, selectedResource]);
  useEffect(() => {
    if (data.length > 0) {
      setPoDescription(data[0]?.poDescription || '');
    }
  }, [data]);
  // useEffect to update filtered data
  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        (searchValue.length === 0 || searchValue.length >= 3) &&
        (searchValueByVendor.length === 0 || searchValueByVendor.length >= 3)
      ) {
        fetchData({
          poNumber: searchValue,
          vendorName: searchValueByVendor,
          startDate,
          endDate,
          pageNumber,
          pageSize,
          sortField,
          sortDirection,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [
    searchValue,
    searchValueByVendor,
    startDate,
    endDate,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (selectedResource) {
      updateDisabledDateRanges();
    }
  }, [selectedResource, jobResources, allResourceData]);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.POs.Title.managePOs}
      </Title>
      <CustomSplitter
        firstPanelContent={LeftPanelContent}
        secondPanelContent={RightPanelContent}
        isSecondPanelVisible={isSecondPanelVisible}
        setIsSecondPanelVisible={setIsSecondPanelVisible}
      />
    </>
  );
};

export default ManagePos;
